import { useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { Button, Input, Label } from '@jira-clone/ui'
import { Mail, Loader2, CheckCircle2, AlertCircle } from 'lucide-react'
import { api } from '../../api/client'
import { getApiErrorMessage } from '../../shared/utils/apiError'

// Self-service SMTP credentials so this user's own Quotations/Proforma
// Invoices/Tax Invoices send from their own real Outlook mailbox (see
// apps/api/src/routes/goodday/invoice-request-email.ts). Unlike every
// other Settings tab, this one is open to every user, not just admins.

interface SmtpSettings {
  host: string
  port: number
  secure: boolean
  username: string
  fromName?: string
  isVerified: boolean
  lastTestedAt?: string
  lastTestError?: string
}

const EMPTY_FORM = {
  host: 'smtp.office365.com',
  port: 587,
  secure: false,
  username: '',
  password: '',
  fromName: '',
}

export default function EmailSettingsPage() {
  const [form, setForm] = useState(EMPTY_FORM)
  const [existing, setExisting] = useState<SmtpSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)

  const load = async () => {
    setLoading(true)
    try {
      const res = await api.get<{ data: SmtpSettings | null }>('/users/me/smtp-settings')
      const data = res?.data
      setExisting(data)
      if (data) {
        setForm({
          host: data.host,
          port: data.port,
          secure: data.secure,
          username: data.username,
          password: '',
          fromName: data.fromName || '',
        })
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'load your email settings'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.host.trim() || !form.port || !form.username.trim() || !form.password.trim()) {
      toast.error('Host, port, username, and password are all required')
      return
    }
    setSaving(true)
    try {
      await api.put('/users/me/smtp-settings', {
        host: form.host.trim(),
        port: Number(form.port),
        secure: form.secure,
        username: form.username.trim(),
        password: form.password,
        fromName: form.fromName.trim() || undefined,
      }, { skipErrorToast: true })
      toast.success('Email settings saved')
      await load()
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'save your email settings'))
    } finally {
      setSaving(false)
    }
  }

  const handleTest = async () => {
    setTesting(true)
    try {
      await api.post('/users/me/smtp-settings/test', undefined, { skipErrorToast: true })
      toast.success('Test email sent - check your inbox')
      await load()
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'send a test email'))
      await load()
    } finally {
      setTesting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="max-w-xl space-y-6">
      <div>
        <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
          <Mail className="h-5 w-5 text-primary" /> Email Settings
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Connect your own Outlook mailbox so Quotations, Proforma Invoices, and Tax Invoices you send go out from your real email address.
        </p>
      </div>

      {existing && (
        <div className={`flex items-start gap-2.5 p-3 rounded-lg border text-sm ${
          existing.isVerified
            ? 'bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-950/30 dark:border-emerald-800 dark:text-emerald-300'
            : 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-300'
        }`}>
          {existing.isVerified ? (
            <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          )}
          <div>
            {existing.isVerified ? (
              <p>Verified - you're ready to send documents from {existing.username}.</p>
            ) : (
              <p>Not verified yet - send a test email below to confirm these credentials actually work.</p>
            )}
            {existing.lastTestError && (
              <p className="mt-1 text-xs opacity-90 break-words">{existing.lastTestError}</p>
            )}
          </div>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2 space-y-1.5">
            <Label className="text-xs font-semibold">SMTP Host</Label>
            <Input
              value={form.host}
              onChange={(e) => setForm({ ...form, host: e.target.value })}
              placeholder="smtp.office365.com"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Port</Label>
            <Input
              type="number"
              value={form.port}
              onChange={(e) => setForm({ ...form, port: Number(e.target.value) })}
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            id="smtp-secure"
            type="checkbox"
            checked={form.secure}
            onChange={(e) => setForm({ ...form, secure: e.target.checked })}
            className="h-4 w-4 rounded border-border"
          />
          <Label htmlFor="smtp-secure" className="text-xs font-medium cursor-pointer">
            Use TLS (port 465) - leave unchecked for STARTTLS (port 587)
          </Label>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">Your Outlook Email</Label>
          <Input
            type="email"
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            placeholder="yourname@company.com"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">
            Password {existing && <span className="font-normal text-muted-foreground">(leave blank to keep the current one)</span>}
          </Label>
          <Input
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder={existing ? '••••••••' : 'App password'}
          />
          <p className="text-[11px] text-muted-foreground">
            Microsoft 365 often requires an app password rather than your normal sign-in password, and SMTP AUTH must be enabled for your mailbox - check with your IT admin if the test email below fails to authenticate.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">Display Name (optional)</Label>
          <Input
            value={form.fromName}
            onChange={(e) => setForm({ ...form, fromName: e.target.value })}
            placeholder="Your name, as the client will see it"
          />
        </div>

        <div className="flex items-center gap-2 pt-2">
          <Button type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
          {existing && (
            <Button type="button" variant="outline" disabled={testing} onClick={handleTest}>
              {testing ? 'Sending...' : 'Send Test Email'}
            </Button>
          )}
        </div>
      </form>
    </div>
  )
}
